import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, LogOut, Upload, Trash2, ExternalLink, ImagePlus } from "lucide-react";
import { API, CATEGORIES, photoSrc } from "@/lib/siteData";

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [photos, setPhotos] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [imageUrl, setImageUrl] = useState("");
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const loadPhotos = useCallback(() => {
    axios.get(`${API}/photos`).then((res) => setPhotos(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(null));
    loadPhotos();
  }, [loadPhotos]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(res.data);
      toast.success("Bienvenue dans votre atelier, Louis.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
    toast.success("Session fermée.");
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Indiquez un titre de chantier");
    if (!file && !imageUrl.trim()) return toast.error("Ajoutez une photo (fichier ou URL)");
    setLoading(true);
    try {
      const form = new FormData();
      form.append("title", title);
      form.append("category", category);
      form.append("description", description);
      form.append("location", location);
      form.append("year", year);
      if (file) form.append("file", file);
      else form.append("image_url", imageUrl);
      await axios.post(`${API}/admin/photos`, form, { withCredentials: true });
      toast.success("Photo ajoutée à la galerie.");
      setTitle("");
      setDescription("");
      setLocation("");
      setImageUrl("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'ajout");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, photoTitle) => {
    if (!window.confirm(`Supprimer « ${photoTitle} » de la galerie ?`)) return;
    try {
      await axios.delete(`${API}/admin/photos/${id}`, { withCredentials: true });
      toast.success("Photo supprimée.");
      loadPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suppression impossible");
    }
  };

  const inputCls =
    "w-full bg-[#FFFFFF] border border-[#1A1714]/15 rounded-sm px-4 py-3 text-sm text-[#1A1714] placeholder-[#8A7E72] focus:outline-none focus:border-[#9E5A38] transition-colors duration-300";

  return (
    <main className="min-h-screen bg-[#FAF7F2] pt-28 pb-20" data-testid="admin-page">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/" className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#8A7E72] hover:text-[#9E5A38] transition-colors duration-300">
          ← Retour au site
        </Link>

        {user === undefined ? null : !user ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 bg-[#FFFFFF] border border-[#1A1714]/10 rounded-sm p-10"
            data-testid="admin-login-dialog"
          >
            <Lock className="w-8 h-8 text-[#9E5A38] mb-6" strokeWidth={1.5} />
            <h1 className="font-serif-display text-3xl font-medium text-[#1C1815]">Espace Maître Artisan</h1>
            <p className="mt-3 text-sm text-[#59524A]">
              Connectez-vous pour ajouter ou retirer des photos de votre galerie de chantiers.
            </p>
            <form onSubmit={handleLogin} className="mt-8 space-y-4">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Adresse e-mail"
                className={inputCls}
                data-testid="admin-email-input"
              />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe"
                className={inputCls}
                data-testid="admin-password-input"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-submit-login-button"
                className="w-full rounded-full bg-[#1A1714] text-[#FAF7F2] py-3.5 text-sm font-medium hover:bg-[#9E5A38] transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Connexion…" : "Accéder à l'atelier"}
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10"
            data-testid="admin-dashboard-container"
          >
            <div className="flex items-start justify-between gap-4 mb-10">
              <div>
                <h1 className="font-serif-display text-3xl font-medium text-[#1C1815]">Gestionnaire de galerie</h1>
                <p className="mt-2 font-mono-tech text-xs text-[#8A7E72]" data-testid="admin-gallery-count">
                  {photos.length} chantier{photos.length > 1 ? "s" : ""} en ligne
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to="/"
                  data-testid="admin-preview-button"
                  className="inline-flex items-center gap-2 rounded-full border border-[#1A1714]/20 px-5 py-2.5 text-sm text-[#574E45] hover:border-[#9E5A38] hover:text-[#9E5A38] transition-colors duration-300"
                >
                  <ExternalLink className="w-4 h-4" /> Voir le site
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="admin-logout-button"
                  className="inline-flex items-center gap-2 rounded-full border border-[#1A1714]/20 px-5 py-2.5 text-sm text-[#574E45] hover:border-[#B91C1C] hover:text-[#B91C1C] transition-colors duration-300"
                >
                  <LogOut className="w-4 h-4" /> Sortir
                </button>
              </div>
            </div>

            <form onSubmit={handleAdd} className="bg-[#FFFFFF] border border-[#1A1714]/10 rounded-sm p-8 space-y-4 mb-12">
              <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#9E5A38] flex items-center gap-2">
                <ImagePlus className="w-4 h-4" /> Nouveau chantier
              </p>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titre du chantier (ex. Rénovation salon haussmannien)"
                className={inputCls}
                data-testid="admin-photo-title-input"
              />
              <div className="grid sm:grid-cols-3 gap-4">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className={inputCls}
                  data-testid="admin-photo-category-select"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Lieu (ex. Paris 7e)"
                  className={inputCls}
                  data-testid="admin-photo-location-input"
                />
                <input
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder="Année"
                  className={inputCls}
                  data-testid="admin-photo-year-input"
                />
              </div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description brève du chantier"
                rows={2}
                className={inputCls}
                data-testid="admin-photo-description-input"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="flex items-center gap-3 border border-dashed border-[#1A1714]/25 rounded-sm px-4 py-3 cursor-pointer hover:border-[#9E5A38] transition-colors duration-300">
                  <Upload className="w-4 h-4 text-[#8A7E72] shrink-0" />
                  <span className="text-sm text-[#574E45] truncate">
                    {file ? file.name : "Choisir un fichier image"}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="admin-photo-file-input"
                  />
                </label>
                <input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="…ou collez une URL d'image"
                  className={inputCls}
                  disabled={!!file}
                  data-testid="admin-photo-url-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-add-photo-button"
                className="w-full rounded-full bg-[#1A1714] text-[#FAF7F2] py-3.5 text-sm font-medium hover:bg-[#9E5A38] transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Ajout en cours…" : "Ajouter à la galerie"}
              </button>
            </form>

            <div className="space-y-3">
              {photos.map((p, i) => (
                <div
                  key={p.id}
                  className="flex items-center gap-4 bg-[#FFFFFF] border border-[#1A1714]/10 rounded-sm p-4"
                  data-testid={`admin-photo-row-${i}`}
                >
                  <img src={photoSrc(p)} alt={p.title} className="w-16 h-16 object-cover rounded-sm shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A1714] truncate">{p.title}</p>
                    <p className="font-mono-tech text-[10px] uppercase tracking-wider text-[#8A7E72] mt-1">
                      {p.category} {p.location ? `— ${p.location}` : ""} {p.year ? `· ${p.year}` : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(p.id, p.title)}
                    data-testid={`admin-delete-photo-button-${i}`}
                    aria-label={`Supprimer ${p.title}`}
                    className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#1A1714]/15 text-[#8A7E72] hover:border-[#B91C1C] hover:text-[#B91C1C] transition-colors duration-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}
